import router from '@/router'
// Direct store-file imports on purpose (not `@/stores`): auth.store and workspace.store import
// this module, so going through the stores index would create an import cycle.
import { useAuthStore } from '@/stores/auth.store.js'
import { useWorkspaceStore } from '@/stores/workspace.store.js'

const COMPLIANCE_PERMISSION = 'view_compliance_alert'

/** Treat empty / route "null" | "undefined" string artifacts as missing. */
function normalizeWorkspaceId(id) {
  if (id == null || id === '' || id === 'null' || id === 'undefined') return null
  return id
}

/**
 * Resolve the active workspace id, most reliable sources first.
 * Persisted user fields come before store/route values so a hard reload
 * resolves before defaultWorkspaceId / selectedWorkspace hydrate.
 * `??` short-circuits, so later sources are only read when earlier ones miss.
 */
export function resolveWorkspaceId() {
  const authStore = useAuthStore()
  const workspaceStore = useWorkspaceStore()

  return (
    normalizeWorkspaceId(authStore.user?.workspace_id) ??
    normalizeWorkspaceId(authStore.user?.default_workspace?.id) ??
    normalizeWorkspaceId(authStore.defaultWorkspaceId) ??
    normalizeWorkspaceId(workspaceStore.selectedWorkspace?.id) ??
    normalizeWorkspaceId(workspaceStore.getWorkspaceID) ??
    normalizeWorkspaceId(router.currentRoute.value.params.workspace_id)
  )
}

/**
 * Home path for a non–super-admin user given a workspace id.
 * Single source of truth so auth + workspace flows can't drift.
 * @returns {string}
 */
export function workspaceHomePath(workspaceId) {
  const base = `/workspaces/${workspaceId}`
  return useAuthStore().allPermissions.includes(COMPLIANCE_PERMISSION)
    ? `${base}/compliance-action-center`
    : `${base}/dashboard`
}

/**
 * Home path for the current non–super-admin user.
 * @returns {string|null} null when no workspace can be resolved.
 */
export function getUserWorkspaceHomePath() {
  const workspaceId = resolveWorkspaceId()
  return workspaceId ? workspaceHomePath(workspaceId) : null
}

/**
 * After create/update workspace:
 * - Super admin → /workspaces
 * - Other users → compliance center or workspace dashboard
 * @param {{ replace?: boolean }} [options]
 */
export function redirectAfterWorkspaceAction({ replace = false } = {}) {
  const authStore = useAuthStore()
  const navigate = replace ? router.replace.bind(router) : router.push.bind(router)

  if (authStore.isSuperAdmin) {
    return navigate('/workspaces')
  }
  return navigate(getUserWorkspaceHomePath() ?? '/workspaces')
}